import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';

import { LabelSection } from '@/sections/insights/queryBuilder/components/QueryVisualizer/sections/LabelSection';
import { LengthInput } from '@/sections/insights/queryBuilder/components/QueryVisualizer/sections/LengthInput';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { Relationship } from '@/types';
import { RELATIONSHIP_PREFIXES, STATIC_RELATIONSHIPS } from '@/types';
import type { FlatGraphEdge } from '@/types/FlatGraph';
import { isValidRelationship } from '@/utils/validation.util';

import { MODAL_CONFIG } from './modalConfig';

interface EdgeDetailsModalProps {
  edgeId: string;
  relationshipDetails: FlatGraphEdge['data'];
  isExpanded: boolean;
  editable: boolean;
  position: { x: number; y: number };
  onClose: () => void;
}

export const EdgeDetailsModal = ({
  edgeId,
  relationshipDetails,
  isExpanded,
  editable,
  onClose,
}: EdgeDetailsModalProps) => {
  const { dispatch } = useQueryData();

  // Local state for editing - initialized from props
  const [editedDetails, setEditedDetails] =
    useState<FlatGraphEdge['data']>(relationshipDetails);

  // Store baseline to detect user changes vs prop updates
  const baselineDetailsRef = useRef<FlatGraphEdge['data']>(relationshipDetails);

  // Compute autocomplete suggestions from static relationships + prefixes
  const relationshipSuggestions = useMemo(() => {
    const staticList = [...STATIC_RELATIONSHIPS];
    const prefixList = RELATIONSHIP_PREFIXES.map(prefix => `${prefix} 📝`);
    return [...staticList, ...prefixList];
  }, []);

  // Auto-save changes to context
  useEffect(() => {
    // Guard clause: skip if not editable
    if (!editable) return;

    // Skip if no user changes (prevents infinite loop on mount)
    if (
      JSON.stringify(editedDetails) ===
      JSON.stringify(baselineDetailsRef.current)
    ) {
      return;
    }

    dispatch({ type: 'UPDATE_EDGE', edgeId, data: editedDetails });
  }, [editedDetails, editable, dispatch, edgeId]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        onClose();
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isExpanded, onClose]);

  if (!isExpanded) return null;

  const labels = Array.isArray(editedDetails.label)
    ? editedDetails.label
    : [editedDetails.label];

  return (
    <div
      data-testid={`edge-details-modal-${edgeId}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`edge-modal-title-${edgeId}`}
      className="bg-chariot-background-primary border-chariot-stroke pointer-events-auto flex flex-col rounded border shadow-lg"
      style={{
        width: `${MODAL_CONFIG.width}px`,
        height: `${MODAL_CONFIG.height}px`,
      }}
    >
      {/* Header - Fixed */}
      <div className="border-chariot-stroke flex flex-shrink-0 items-center justify-between border-b p-2">
        <h3
          id={`edge-modal-title-${edgeId}`}
          className="text-chariot-text-primary text-xs font-semibold"
        >
          Relationship
        </h3>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-chariot-text-secondary hover:text-chariot-text-primary"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 space-y-4 overflow-y-auto p-2">
        {/* Relationship Type Section */}
        <LabelSection<Relationship>
          labels={labels}
          editable={editable}
          onChange={newLabels =>
            setEditedDetails({ ...editedDetails, label: newLabels })
          }
          validateLabel={isValidRelationship}
          placeholder="Type relationship type and press Enter..."
          suggestedLabels={relationshipSuggestions}
        />

        {/* Constraints Section */}
        <div className="space-y-4">
          <h3 className="text-chariot-text-primary text-xs font-semibold">
            Constraints
          </h3>

          {/* Length Field */}
          <LengthInput
            value={editedDetails.length}
            editable={editable}
            onChange={length => setEditedDetails({ ...editedDetails, length })}
          />

          {/* Boolean Toggles */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editedDetails.optional || false}
                onChange={e =>
                  setEditedDetails({
                    ...editedDetails,
                    optional: e.target.checked,
                  })
                }
                disabled={!editable}
                className="border-chariot-stroke rounded"
              />
              <span className="text-chariot-text-primary text-[10px]">
                Optional
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editedDetails.select_one || false}
                onChange={e =>
                  setEditedDetails({
                    ...editedDetails,
                    select_one: e.target.checked,
                  })
                }
                disabled={!editable}
                className="border-chariot-stroke rounded"
              />
              <span className="text-chariot-text-primary text-[10px]">
                Select One (LIMIT 1)
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editedDetails.not_exists || false}
                onChange={e =>
                  setEditedDetails({
                    ...editedDetails,
                    not_exists: e.target.checked,
                  })
                }
                disabled={!editable}
                className="border-chariot-stroke rounded"
              />
              <span className="text-chariot-text-primary text-[10px]">
                Not Exists
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
